import { createContext, useContext, useState, ReactNode, useCallback, useEffect } from "react";
import type { FrictionEntry } from "./frictionLogTypes";
import { FrictionLogService } from "@/services/friction-log.service";
import { useDeferHeavyProvidersForCurrentRoute } from "@/lib/routeScope";

interface FrictionLogContextValue {
  entries: FrictionEntry[];
  addEntry: (entry: FrictionEntry) => void;
  updateEntry: (id: string, updates: Partial<Pick<FrictionEntry, "status" | "adminNotes">>) => void;
}

export const FrictionLogContext = createContext<FrictionLogContextValue | undefined>(undefined);

export function FrictionLogProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<FrictionEntry[]>([]);
  const deferHeavyProvider = useDeferHeavyProvidersForCurrentRoute();

  // ── Load from DB on mount ───────────────────────────────────────────────────
  useEffect(() => {
    if (deferHeavyProvider) return;

    FrictionLogService.loadEntries().then((loaded) => {
      if (loaded.length > 0) setEntries(loaded);
    });
  }, [deferHeavyProvider]);

  const addEntry = useCallback((entry: FrictionEntry) => {
    setEntries((prev) => [entry, ...prev]);
    FrictionLogService.saveEntry(entry);
  }, []);

  const updateEntry = useCallback((id: string, updates: Partial<Pick<FrictionEntry, "status" | "adminNotes">>) => {
    setEntries((prev) =>
      prev.map((e) => (e.id === id ? { ...e, ...updates } : e))
    );
    FrictionLogService.updateEntry(id, updates);
  }, []);

  return (
    <FrictionLogContext.Provider value={{ entries, addEntry, updateEntry }}>
      {children}
    </FrictionLogContext.Provider>
  );
}

export function useFrictionLog() {
  const ctx = useContext(FrictionLogContext);
  if (!ctx) throw new Error("useFrictionLog must be inside FrictionLogProvider");
  return ctx;
}
